import { defineComponent, h, nextTick } from 'vue';
import {
    QLinearProgress, QIcon, QBtn, QTabs, QTab, QTabPanels, QTabPanel,
    QTree, QMenu, QList, QItem, QItemSection, QItemLabel, QSeparator,
    QToolbarTitle, QSpace,
} from 'quasar';
import { shapes } from '@joint/core';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import JointPaper from './vuecontrols/JointPaper.vue';
import AceEditor from './3rdParty/AceEditor.jsx';
import PropertyGrid from './form/PropertyGrid.jsx';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

function toHex(n) {
    n = parseInt(n, 10);
    if (isNaN(n)) return "00";
    n = Math.max(0, Math.min(n, 255));
    return "0123456789ABCDEF".charAt((n - n % 16) / 16)
        + "0123456789ABCDEF".charAt(n % 16);
}

function rgbToHex(R, G, B) {
    return toHex(R) + toHex(G) + toHex(B)
}

const empty_tree =
    [
        {
            id: 1,
            name: 'Unnamed.prj',
            children: [
                {
                    id: 2,
                    name: 'API References',
                    children: [
                        {
                            id: 3,
                            name: 'System'
                        },
                        {
                            id: 4,
                            name: 'Platform'
                        }
                    ]
                },
                {
                    id: 5,
                    name: 'Project References',
                    children: [],
                },
                {
                    id: 6,
                    name: 'Resources',
                    children: [],
                },
                {
                    id: 7,
                    name: 'Enums',
                    children: [],
                },
                {
                    id: 8,
                    name: 'Application',
                    children: [],
                }
            ]
        }
    ];


// The arghandler is an object used with a Proxy to control reading and writing
// to an icon which has all parameters saved in an array called Arguments. Each
// param has a PropertyName and PropertyValue as opposed to being an object
// with first class properties.
const arghandler = {
    get(target, prop, receiver) {
        const f = target.find(a => a.PropertyName == prop);
        if (f)
            return f.PropertyValue;

        return undefined;
    },
    set(target, prop, value) {
        const f = target.find(a => a.PropertyName == prop);
        if (f)
            f.PropertyValue = value;

        return true;
    },
    has(target, prop) {
        return !!target.find(a => a.PropertyName == prop);
    }
}

export default defineComponent({
    name: 'flow-chart',
    mixins: [BaseComponent],
    components: {
        JointPaper,
        AceEditor,
        PropertyGrid,
        Splitpanes,
        Pane,
        QLinearProgress,
        QIcon,
        QBtn,
        QTabs,
        QTab,
        QTabPanels,
        QTabPanel,
        QTree,
        QMenu,
        QList,
        QItem,
        QItemSection,
        QItemLabel,
        QSeparator,
        QToolbarTitle,
        QSpace,
    },
    data: function () {
        return {
            next_id: 1,
            selected_menu: -1,
            selected_tab: 0,
            loading: true,
            treeSelected: null,
            treeExpanded: [1],
            treeNodes: empty_tree,

            background: {
                color: '#fffffb'
            },
            gridSize: 10,
            drawGrid: true,
            model: null,

            tabs: [],

            closing_tab: -1,

            internal_apis: {},
            properties_schema: null,
            properties_object: null,

            project_references: {},
        }
    },
    created() {
        if (this.controlData.ProjectName)
            this.projectnameexpn = utils.compileObject(this, this.controlData.ProjectName);
    },
    computed: {
        toolBar: function () {
            let loading;
            if (this.loading)
                loading = (
                    <QLinearProgress
                        indeterminate
                        color="yellow-8"
                    />
                );

            return (
                <div>
                    {loading}
                    <div style="min-height: 60px; border: 1px solid silver; border-radius: 8px; margin: 3px;">
                    </div>
                </div>
            );
        },
        mainTabbedArea: function () {
            const tab_titles = [];
            const tab_body = [];

            for (let i = 0; i < this.tabs.length; i++) {
                const t = this.tabs[i];

                let content;
                let icon;
                switch (t.obj.$type) {
                    case 'MethodLib.Serialization.ProjectClassFlowchart, CompilerCore':
                        icon = (
                            <QIcon name="mdi-sitemap" size="xs" class="q-mr-xs" />
                        );
                        content = (
                            <JointPaper
                                class={{ [`c-FlowChartPaper_${t.Name}`]: true }}
                                width="5000px"
                                height="5000px"
                                background={this.background}
                                gridSize={this.gridSize}
                                drawGrid={this.drawGrid}
                                onInit={(graph, paper) => this.setupGraph(graph, paper, t.obj.Objects)}>
                            </JointPaper>
                        );
                        break;

                    case 'MethodLib.Serialization.ProjectClassCode, CompilerCore':
                        icon = (
                            <QIcon name="mdi-code-braces-box" size="xs" class="q-mr-xs" />
                        );
                        content = (
                            <AceEditor
                                lang="csharp"
                                content={t.obj.CodeBlock}
                                onChange-content={(value) => this.onCodeBlockChange(t, value)}>
                            </AceEditor>
                        );
                        break;
                }

                tab_titles.push(
                    <QTab
                        key={t.id}
                        name={i}
                        class="text-none"
                        style="min-width: 0;"
                        onAuxclick={(e) => { if (e.button === 1) this.closeTab(e, t, i); }}
                    >
                        <span style="display: inline-flex; align-items: center; white-space: nowrap; gap: 4px;">
                            {icon}
                            <span>{t.Name.split('/').pop()}</span>
                            <QIcon
                                name="mdi-close-circle-outline"
                                size="xs"
                                style="cursor: pointer;"
                                onClick={(e) => { e.stopPropagation(); this.closeTab(e, t, i); }}
                            />
                        </span>
                    </QTab>
                );

                tab_body.push(
                    <QTabPanel key={t.id} name={i} class="q-pa-none" style="height: 1px;">
                        <div style="flex-grow: 1; height: 1px; overflow: auto;">
                            {content}
                        </div>
                    </QTabPanel>
                );
            }

            return ([
                <QTabs
                    modelValue={this.selected_tab}
                    onUpdate:modelValue={(t) => this.changeTab(t)}
                    style="flex-grow: 0;"
                    align="left"
                    dense
                >
                    {tab_titles}
                </QTabs>
                ,
                <QTabPanels
                    modelValue={this.selected_tab}
                    onUpdate:modelValue={(t) => this.changeTab(t)}
                    style="flex-grow: 1; height: 1px; display: flex; flex-direction: column;"
                    class="q-pa-none"
                    animated
                >
                    {tab_body}
                </QTabPanels>
            ]);
        },
        projectTree: function () {
            const renderNodeHeader = ({ node }) => {
                const isActive = this.treeSelected === node.id;
                const hasChildren = Array.isArray(node.children) && node.children.length > 0;
                return (
                    <div
                        class="q-tree__node-header"
                        role="button"
                        tabindex={0}
                        style="display: flex; align-items: center; width: 100%; cursor: pointer;"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasChildren)
                                this.toggleTreeExpanded(node.id);
                            this.treeSelected = node.id;
                        }}
                        onKeydown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (hasChildren)
                                    this.toggleTreeExpanded(node.id);
                                this.treeSelected = node.id;
                            }
                        }}
                    >
                        {node.icon ? <QIcon name={node.icon} class="q-mr-xs" /> : null}
                        <span style="flex: 1;">{node.name}</span>
                        {isActive && node.editable ?
                            <QBtn flat dense round size="sm" icon="mdi-pencil" onClick={(e) => this.open(e, node)} />
                            : null}
                    </div>
                );
            };

            return (
                <QTree
                    dense
                    noConnectors
                    nodes={this.treeNodes}
                    node-key="id"
                    label-key="name"
                    expanded={this.treeExpanded}
                    onUpdate:expanded={(keys) => { this.treeExpanded = keys; }}
                    selected={this.treeSelected}
                    onUpdate:selected={(key) => { this.treeSelected = key; }}
                    v-slots={{ 'default-header': renderNodeHeader }}
                />
            );
        },
        properties: function () {
            if (!this.properties_schema)
                return <span>Nothing selected</span>;

            return (
                <PropertyGrid
                    name="Noname"
                    root={this.Root}
                    parentType="VerticalStack"
                    schema={this.properties_schema}
                    form={["*"]}
                    cmodel={this.properties_object}
                    child={0}>
                </PropertyGrid>
            );
        },

        topArea: function () {
            return this.toolBar;
        },
        mainArea: function () {
            return this.mainTabbedArea;
        },
        rightTopArea: function () {
            return this.projectTree;
        },
        rightBottomArea: function () {
            return this.properties;
        },

        recentsMenu: function () {
            return [
                <QItem clickable v-ripple onClick={() => { }}>
                    <QItemSection>
                        <QItemLabel>Last item 1</QItemLabel>
                    </QItemSection>
                </QItem>,

                <QItem clickable v-ripple onClick={() => { }}>
                    <QItemSection>
                        <QItemLabel>Last item 2</QItemLabel>
                    </QItemSection>
                </QItem>
            ];
        },
        fileMenu: function () {
            return [
                <QItem key={0} clickable v-ripple onClick={() => { }}>
                    <QItemSection avatar>
                        <QIcon name="mdi-folder-open" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>Open</QItemLabel>
                    </QItemSection>
                </QItem>,

                <QSeparator key="sep1" />,

                <QItem key={1} clickable v-ripple onClick={() => { }}>
                    <QItemSection avatar>
                        <QIcon name="mdi-content-save" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>Save</QItemLabel>
                    </QItemSection>
                </QItem>,

                <QItem key={2} clickable v-ripple onClick={() => { }}>
                    <QItemSection avatar />
                    <QItemSection>
                        <QItemLabel>Save As...</QItemLabel>
                    </QItemSection>
                </QItem>,

                <QSeparator key="sep2" />,

                <QItem key={3} clickable v-ripple>
                    <QItemSection avatar />
                    <QItemSection>
                        <QItemLabel>Recent</QItemLabel>
                    </QItemSection>
                    <QItemSection side>
                        <QIcon name="mdi-menu-right" />
                    </QItemSection>
                    <QMenu anchor="top end" self="top start" auto-close>
                        <QList dense class="bg-grey-3">
                            {this.recentsMenu}
                        </QList>
                    </QMenu>
                </QItem>,

                <QSeparator key="sep3" />,

                <QItem key={4} clickable v-ripple onClick={() => { }}>
                    <QItemSection avatar>
                        <QIcon name="mdi-close-box-outline" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>Close</QItemLabel>
                    </QItemSection>
                </QItem>,
            ];
        },
        topMenuBar: function () {
            const title = this.model ? this.model.Name : 'Unnamed Project';

            return (
                <div style="display: flex; align-items: center;">
                    <QBtn flat label="File">
                        <QMenu anchor="bottom start" self="top start">
                            <QList dense class="bg-grey-3">
                                {this.fileMenu}
                            </QList>
                        </QMenu>
                    </QBtn>

                    <QSpace />
                    <QToolbarTitle>{title}</QToolbarTitle>
                    <QSpace />

                    <QBtn flat round icon="mdi-close-box-outline" />
                </div>
            );
        },
    },
    methods: {
        preRenderComplete() {
            this.finishRenderHandler(this);
            this.Refresh();
        },
        async Refresh() {
            let fn;

            if (this.projectnameexpn)
                fn = utils.evaluate(this.projectnameexpn, this);

            if (fn)
                this.model = await utils.api.get(`Apps/Project/${fn}`);
            else if (this.Query('fn'))
                this.model = await utils.api.get(`Apps/Project/${this.Query('fn')}`);

            this.scanAllMembers(this.model);

            await this.loadProjectReferences(this.model);

            if (this.model)
                this.buildProjectTreeNodes();
            else {
                this.treeNodes = empty_tree;
                this.treeExpanded = [1];
            }

            this.loading = false;
        },
        buildProjectTreeNodes() {
            this.next_id = 1;

            const items = [
                {
                    id: this.next_id++,
                    name: this.model.Name,
                    icon: 'mdi-file-table-box',
                    children: [
                        {
                            id: this.next_id++,
                            name: 'API References',
                            icon: 'mdi-folder',
                            children: this.model.References.map(a => ({
                                id: this.next_id++,
                                name: a,
                            })),
                        },
                        {
                            id: this.next_id++,
                            name: 'Project References',
                            icon: 'mdi-folder',
                            children: [],
                        },
                        {
                            id: this.next_id++,
                            name: 'Resources',
                            icon: 'mdi-folder',
                            children: [],
                        },
                        {
                            id: this.next_id++,
                            name: 'Enums',
                            icon: 'mdi-folder',
                            children: this.model.Classes.filter(c => c.$type == 'MethodLib.Serialization.ProjectClassEnum, CompilerCore').map(c => ({
                                id: this.next_id++,
                                name: c.ClassName,
                                obj: c,
                                children: c.Values.map(v => ({
                                    id: this.next_id++,
                                    name: v,
                                }))
                            })),
                        },
                    ]
                }
            ];

            for (let i = 0; i < this.model.Classes.length; i++) {
                const c = this.model.Classes[i];
                if (c.$type == 'MethodLib.Serialization.ProjectClassEnum, CompilerCore')
                    continue;

                let members = (c.Members ? c.Members.filter(m => m.$type != 'MethodLib.Serialization.ProjectClassField, CompilerCore').map(m => ({
                    id: this.next_id++,
                    name: m.Name,
                    icon: m.$type == 'MethodLib.Serialization.ProjectClassFlowchart, CompilerCore' ? 'mdi-sitemap' : 'mdi-code-braces-box',
                    obj: m,
                    editable: true,
                    children: m.Locals ? m.Locals.map(o => ({
                        id: this.next_id++,
                        name: o.Name,
                        obj: o,
                    })) : [],
                })) : []);

                members = members.slice().sort((a, b) => a.name.localeCompare(b.name));

                items[0].children.push({
                    id: this.next_id++,
                    name: c.ClassName,
                    obj: c,
                    icon: 'mdi-application',
                    children: [
                        {
                            id: this.next_id++,
                            name: 'Data Fields',
                            icon: 'mdi-folder',
                            children: c.Members.filter(m => m.$type == 'MethodLib.Serialization.ProjectClassField, CompilerCore').map(m => ({
                                id: this.next_id++,
                                name: m.Name,
                            }))
                        },
                        ...members],
                });
            }

            this.treeNodes = items;
            this.treeExpanded = items.length > 0 ? [items[0].id] : [];
        },
        toggleTreeExpanded(nodeId) {
            const idx = this.treeExpanded.indexOf(nodeId);
            if (idx >= 0)
                this.treeExpanded = this.treeExpanded.filter((_, i) => i !== idx);
            else
                this.treeExpanded = [...this.treeExpanded, nodeId];
        },
        async loadProjectReferences(model) {
            if (Array.isArray(model.ProjectRefs) && model.ProjectRefs.length > 0) {
                for (let i = 0; i < model.ProjectRefs.length; i++) {
                    const fn = model.ProjectRefs[i];
                    if (fn in this.project_references)
                        continue;

                    let proj;
                    if (fn.length > 1 && fn.substr(0, 1) == '$')
                        proj = await utils.api.get(`Apps/Projects/Published/${fn.substr(1)}`);
                    else
                        proj = await utils.api.get(`Apps/Project/${fn}`);

                    if (proj) {
                        this.scanAllMembers(proj);
                        this.project_references[fn] = proj;
                        await this.loadProjectReferences(proj);
                    }
                }
            }
        },
        changeTab(index) {
            utils.debug(`try changeTab(index:${index})`);
            if (index === undefined || index == this.closing_tab) {
                this.closing_tab = -1;
                return;
            }

            this.selected_tab = index;
        },
        open(e, item) {
            e.cancelBubble = true;
            e.stopPropagation();

            for (let i = 0; i < this.tabs.length; i++) {
                const t = this.tabs[i];

                if (t.id == item.id) {
                    this.selected_tab = i;
                    return;
                }
            }

            this.tabs.push({
                id: item.id,
                Name: item.name,
                obj: item.obj,
            });

            this.selected_tab = this.tabs.length - 1;
        },
        onCodeBlockChange(tab, value) {
            tab.obj.CodeBlock = value;
        },
        closeTab(e, tab, index) {
            e.cancelBubble = true;
            e.stopPropagation();

            this.closing_tab = index;

            const old = this.selected_tab;

            this.tabs.splice(index, 1);

            nextTick(() => {
                if (old >= index) {
                    this.selected_tab = old - 1;
                }
            });
        },

        scanAllMembers(model) {
            for (let i = 0; i < model.Classes.length; i++) {
                const c = model.Classes[i];
                if (c.Members)
                    for (let j = 0; j < c.Members.length; j++) {
                        const m = c.Members[j];
                        if (m.Key) {
                            this.saveSchemaFromMethod(m);
                        }
                    }
            }
        },

        blank_pointerdown(evt, x, y) {
            if (this.selected)
                this.selected.unhighlight(this.selected.el);

            this.selected = null;
            this.properties_schema = null;
            this.properties_object = null;
        },
        async cell_pointerdown(cellView, evt, x, y) {
            if (this.selected)
                this.selected.unhighlight(this.selected.el);

            cellView.highlight(cellView.el);
            this.selected = cellView;

            const method = cellView.model.attributes.Method;
            const type = encodeURIComponent(method.$type);
            const key = method.ImplementationKey ? encodeURIComponent(method.ImplementationKey) : '';
            let schema = method.ImplementationKey ? this.internal_apis[method.ImplementationKey] : null;
            if (!schema)
                schema = await utils.api.get(`Apps/WebFlowHelpers/GetIconSchema?Type=${type}&APIKey=${key}`);

            this.properties_schema = this.getWrapperSchema(schema);
            this.properties_object = this.getWrapperObject(this.getModelForIcon(method), cellView.model.attributes);
        },
        cell_pointerup(cellView, evt, x, y) {
            if (this.changed) {
            }

            this.changed = false;
        },
        cell_pointerdblclick(cellView, evt, x, y) {
            const method = cellView.model.attributes.Method;
        },

        getWrapperSchema(schema) {
            const p = {
                type: 'object',
                properties: {
                    API: {
                        type: 'object',
                        properties: {
                            Id: {
                                type: 'string',
                                readonly: true,
                            },
                            Namespace: {
                                type: 'string',
                                readonly: true,
                                condition: 'ParentModel.Namespace'
                            },
                            Name: {
                                type: 'string',
                                readonly: true,
                            }
                        }
                    },
                    Arguments: schema,
                }
            };

            return p;
        },
        getWrapperObject(model, icon) {
            const o = {
                API: {
                    Id: icon.ID,
                    Namespace: icon.Method.Namespace,
                    Name: icon.Method.Name,
                },
                Arguments: model,
            };

            return o;
        },
        async getSchemaForCLRType(type) {
            switch (type) {
                case 'string':
                case 'System.String':
                    return {
                        type: 'string',
                    };
                case 'bool':
                case 'System.Boolean':
                    return {
                        type: 'boolean',
                    };
                case 'float':
                case 'double':
                case 'System.Float':
                case 'System.Double':
                    return {
                        type: 'number',
                    };
                case 'int':
                case 'long':
                case 'System.Int16':
                case 'System.Int32':
                case 'System.Int64':
                    return {
                        type: 'integer',
                    };
                case 'object':
                case 'CompilerCore.Evaluators.JsonObj':
                    return {
                        type: 'object',
                    };
                case 'DateTime':
                case 'System.DateTime':
                    return {
                        type: 'string',
                        format: 'date-time',
                    };

                default:
                    return await utils.api.get(`Apps/WebFlowHelpers/GetIconSchema?Type=${encodeURIComponent(type)}`);
            }
        },
        async getSchemaForUserType(ownerid, type, projectName) {
            return { type: 'object', properties: {} };
        },
        async saveSchemaFromMethod(item) {
            const schema = {
                $schema: `${location.protocol}//${location.hostname}/schema/public/v1/schema-draft04#`,
                id: `${location.protocol}//${location.hostname}/Apps/APISchema/${item.ImplementationKey}`,
                type: 'object',
                description: item.Description,
                properties: {},
            };

            for (let i = 0; i < item.Arguments.length; i++) {
                const a = item.Arguments[i];
                if (a.Hidden) continue;

                let arg = {
                    type: 'string',
                    title: `${a.Name}: ${a.ArgType}`
                };

                schema.properties[a.Name] = arg;

                if (a.DefaultValue)
                    arg.default = a.DefaultValue;
                if (a.Description)
                    arg.description = a.Description;

                if (a.Choices && a.Choices.length > 0) {
                    arg.enum = a.Choices;
                }
                else if (a.ArgType == 'bool')
                {
                    arg.titleMap = [{ name: 'true', value: 'true' }, { name: 'false', value: 'false' }];
                    arg.formatData = { AllowCustomInput: true };
                }
                else if (a.LookupAPI) {
                    arg.formatData = {};
                    arg.format = 'LookupList';
                    arg.formatData.LookupURL = a.LookupAPI;
                    arg.formatData.AllowCustomInput = true;
                    if (a.LookupAPIDisplayField) {
                        arg.formatData.DisplayType = 'Fieldname';
                        arg.formatData.DisplayField = a.LookupAPIDisplayField;
                    }
                    if (a.LookupAPIValueField) {
                        arg.formatData.ValueType = 'Fieldname';
                        arg.formatData.ValueField = a.LookupAPIValueField;
                    }
                }
            }

            this.internal_apis[item.Key] = schema;
        },
        getModelForIcon(item) {
            if (!item.Arguments)
                return item;

            return new Proxy(item.Arguments, arghandler);
        },

        setupGraph(graph, paper, objects) {
            const c = this;
            this.graph = graph;
            this.paper = paper;

            paper.on('blank:pointerdown', this.blank_pointerdown);
            paper.on('cell:pointerdown', this.cell_pointerdown);
            paper.on('cell:pointerup', this.cell_pointerup);
            paper.on('cell:pointerdblclick', this.cell_pointerdblclick);

            graph.on('change:position change:size change:source change:target change:vertices add remove', function (cell) {
                c.changed = true;
            });

            this.loadFlowchart(graph, paper, objects);
        },
        loadFlowchart(graph, paper, items) {
            graph.clear();

            const item_ids = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                let label = item.Label || item.Text;
                if (label == 'DevClient.StartItem') label = 'Start()';

                if (item.Item && item.Item.ImplementationKey)
                    this.saveSchemaFromMethod(item.Item);

                let type = item.$type.split('.')[2].split(',')[0];
                switch (type) {
                    case 'PropertiesGraphicsMethod':
                        const m1 = new shapes.standard.Rectangle();
                        m1.position(item.Left, item.Top);
                        m1.resize(item.Right - item.Left, item.Bottom - item.Top)
                        m1.attr({ body: { fill: 'blue' }, label: { text: 'unused', fill: 'white' } });

                        label = item.Item.Name + '()';

                        const color = rgbToHex(item.ObjectColor.R, item.ObjectColor.G, item.ObjectColor.B);
                        m1.attr({
                            body: { fill: '#f2f2f2', rx: 5, ry: 5, strokeWidth: 0.5, stroke: '#' + color },
                            label: {
                                textWrap: { text: label, width: -10, height: 25 },
                                fill: '#' + color,
                                fontSize: 12,
                                fontWeight: 'normal'
                            }
                        });

                        m1.prop({ Method: item.Item, ID: item.ID });

                        m1.addTo(graph);

                        item_ids[item.ID] = { id: m1.id, obj: m1 };
                        break;

                    case 'PropertiesGraphicsText':
                        const rect = new shapes.standard.Rectangle();
                        rect.position(item.Left, item.Top);
                        rect.resize(item.Right - item.Left, item.Bottom - item.Top);
                        rect.attr({
                            body: { fillOpacity: 0, rx: 5, ry: 5, strokeWidth: 0, stroke: 'black' },
                            label: {
                                textWrap: { text: label, width: -15 },
                                fill: '#3498DB',
                                fontSize: 12,
                                fontWeight: 'normal'
                            }
                        });
                        rect.addTo(graph);
                        break;
                }
            }

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (!item.Links) continue;

                const graph_item = item_ids[item.ID].obj;

                for (let j = 0; j < item.Links.length; j++) {
                    const target = item_ids[item.Links[j].TargetID].obj;

                    const link = new shapes.standard.Link();
                    link.source(graph_item);
                    link.target(target);
                    link.attr('line/stroke', 'blue');
                    link.attr({
                        line: {
                            stroke: 'blue',
                            strokeWidth: 0.5,
                        },
                    });
                    if (item.Links[j].Label)
                        link.appendLabel({
                            attrs: {
                                text: {
                                    fill: 'blue',
                                    text: item.Links[j].Label
                                },
                                body: {
                                    fill: 'white',
                                    strokeWidth: 0.5,
                                    stroke: 'silver',
                                }
                            },
                            position: {
                                distance: 0.5,
                                args: {
                                    keepGradient: true,
                                    ensureLegibility: true
                                }
                            }
                        });

                    link.prop({
                        Label: item.Links[j].Label,
                        SourceVar: item.Links[j].SourceVar,
                        Criteria: item.Links[j].Criteria
                    });
                    link.connector('rounded');
                    link.connector('jumpover', { size: 10 });

                    link.addTo(graph);
                }
            }
        },

        getFlowchartObjects() {
            const cells = this.graph.getCells();
            const objects = [];

            for (let i = 0; i < cells.length; i++) {
                let cell = cells[i];
                if (cell.attributes.Method) {
                    let item = {
                        $type: 'MethodLib.Serialization.PropertiesGraphicsMethod, CompilerCore',
                        Item: cell.attributes.Method,
                        Links: [],
                        ID: cell.attributes.ID,
                        Label: cell.attributes.attrs.text.text,
                        LineWidth: 0.5,
                        ObjectColor: {
                            R: 30, G: 144, B: 255, A: 255
                        },
                        Left: cell.attributes.position.x,
                        Top: cell.attributes.position.y,
                        Right: cell.attributes.position.x + cell.attributes.size.width,
                        Bottom: cell.attributes.position.y + cell.attributes.size.height,
                        Trace: false,
                        Break: 0
                    };
                    let links = getLinksForId(cell.attributes.id);
                    for (let j = 0; j < links.length; j++) {
                        let link = links[j];
                        let targetCell = this.graph.getCell(link.attributes.target.id);
                        item.Links.push({
                            Label: link.attributes.Label,
                            SourceVar: link.attributes.SourceVar,
                            Criteria: link.attributes.Criteria,
                            TargetID: targetCell.attributes.ID
                        })
                    }

                    objects.push(item);
                }
            }
            return objects;
        },
    },
    props: {
    },
    render() {
        const style = {
            ...this.sizeStyle,
            ...utils.resolveStyleHints(this.styleHints, this),
            display: 'flex',
            flexDirection: 'column',
            height: '1px',
        };

        return (
            <div
                v-show={this.isvisible}
                style={style}
                class={{ 'default-theme': true, 'c-FlowChart': true, [`c-name-${this.name || 'unnamed'}`]: true }}>

                {this.topMenuBar}

                {this.topArea}

                <Splitpanes
                    horizontal={false}
                    push-other-panes={true}
                    first-splitter={false}
                    style="height: 1px; flex-grow: 1;"
                >
                    <Pane
                        style="display: flex; flex-direction: column;"
                        key="main"
                        size={75}
                        dbl-click-splitter={true}
                    >
                        {this.mainArea}
                    </Pane>

                    <Pane
                        key="right"
                        size={25}
                        dbl-click-splitter={true}
                    >
                        <Splitpanes
                            horizontal={true}
                            push-other-panes={true}
                            first-splitter={false}
                        >
                            <Pane key="right_top" size={50} style="overflow: auto;">
                                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: '1', height: '100px' }}>
                                    {this.rightTopArea}
                                </div>
                            </Pane>

                            <Pane key="right_bottom" size={50} style="overflow: auto;">
                                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: '1', height: '100px' }}>
                                    {this.rightBottomArea}
                                </div>
                            </Pane>
                        </Splitpanes>
                    </Pane>
                </Splitpanes>
            </div>
        );
    }
});
